import {
  faEye,
  faCircleNotch,
  faCircleInfo,
  faBan,
} from "@fortawesome/pro-regular-svg-icons";
import { Cell, ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { twJoin } from "tailwind-merge";

import { DecisionHistoryRecordV2 } from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { Flow, FlowVersion, GenericObject } from "src/api/flowTypes";
import { DecisionEnvironment } from "src/api/types";
import { Icon } from "src/base-components/Icon";
import { RawCell } from "src/dataTable/RawCell";
import { ResultCell } from "src/dataTable/ResultCell";
import { TruncatedIdPill } from "src/decisionsOverview/TruncatedIdPill";
import { Tooltip } from "src/design-system/Tooltip";
import { singleFlowGetParentFlows } from "src/flow/Model";
import { getSourceDetails } from "src/flow/decisionHistory/DecisionHistoryUtils";
import {
  SharedDecisionHistoryStatus,
  DecisionStatusCode,
  POSSIBLE_DECISION_STATUS_CODES,
} from "src/flow/decisionHistory/SharedStatusColumn";
import { SourcePill } from "src/flow/decisionHistory/SourcePill";
import { Job } from "src/jobs/types";
import { formatDate } from "src/utils/datetime";
import { RowMenu } from "src/versionDecisionHistory/RowMenu";
import { getDisableReasonMessage } from "src/versionDecisionHistory/Table";
import { DecisionHistoryTableFilter } from "src/versionDecisionHistory/TableFilter";

export const CONTROL_COLUMN_ID = "control";

const columnHelper =
  createColumnHelper<DecisionHistoryRecordV2<GenericObject>>();

const HeaderCell: React.FC<{
  children?: React.ReactNode;
  className: string;
}> = ({ children, className }) => (
  <div
    className={twJoin(
      "py-1.5 pr-4 text-left text-gray-800 font-inter-medium-12px",
      className,
    )}
  >
    {children}
  </div>
);

type Args = {
  orgId: string;
  wsId: string;
  flow: Flow;
  version: FlowVersion;
  jobs: Job[];
  env: DecisionEnvironment;
  selectedRowIsLoading: boolean;
  shouldRowBeDisabled: (
    record: DecisionHistoryRecordV2,
  ) => "internal_error" | "outdated" | false;
  onAddTestCase: (decisionId: string) => void;
  onCancelDecision: (flowSlug: string, decisionId: string) => void;
  cancellingDecisionIds?: string[];
  statusCodeFilter: DecisionStatusCode | undefined;
  setStatusCodeFilter: (
    statusCodeFilter: DecisionStatusCode | undefined,
  ) => void;
  isPowertoolsEnabled?: boolean;
  xrayTraceURL?: string | null;
};

export const getVersionDecisionHistoryColumns = ({
  orgId,
  wsId,
  flow,
  version,
  jobs,
  env,
  selectedRowIsLoading,
  shouldRowBeDisabled,
  onAddTestCase,
  onCancelDecision,
  cancellingDecisionIds,
  statusCodeFilter,
  setStatusCodeFilter,
  isPowertoolsEnabled,
  xrayTraceURL,
}: Args) => {
  const statusFilterOptions = POSSIBLE_DECISION_STATUS_CODES.map((option) => ({
    ...option,
    disabled: false,
  }));
  const parentFlows = singleFlowGetParentFlows(flow);
  const parentVersions = version.parent_versions ?? [];

  const getRowControls = (
    cell: Cell<DecisionHistoryRecordV2<GenericObject>, unknown>,
  ) => {
    const isSelected = cell.row.getIsSelected();
    const disabledReason = shouldRowBeDisabled(cell.row.original);
    const isChildDecision = !!cell.row.original.parent_decision_id;

    return (
      <div className="justify-right ml-auto flex w-12 flex-row items-center">
        {(() => {
          if (selectedRowIsLoading && isSelected) {
            return (
              <span className="inline-block animate-spin justify-end align-middle text-indigo-400">
                <Icon icon={faCircleNotch} size="xs" />
              </span>
            );
          }

          return disabledReason ? (
            <Tooltip
              placement="top"
              title={getDisableReasonMessage(disabledReason)}
              asChild
            >
              <span className="cursor-default justify-end align-middle text-gray-500 opacity-0 group-hover/row:opacity-100">
                <Icon icon={faBan} size="sm" />
              </span>
            </Tooltip>
          ) : (
            <span
              className={twJoin(
                "inline-block justify-end align-middle text-gray-500",
                isSelected
                  ? "text-indigo-500"
                  : "opacity-0 group-hover/row:opacity-100",
              )}
            >
              <Icon icon={faEye} size="sm" />
            </span>
          );
        })()}
        <RowMenu
          cancellingDecisionIds={cancellingDecisionIds}
          cell={cell}
          currentDecision={
            cell.getContext().table.options.meta?.currentDecision
          }
          isChildDecision={isChildDecision}
          isPowertoolsEnabled={Boolean(isPowertoolsEnabled)}
          isSelected={isSelected}
          xrayTraceURL={xrayTraceURL}
          onAddTestCase={onAddTestCase}
          onCancelDecision={onCancelDecision}
        />
      </div>
    );
  };

  return [
    columnHelper.accessor("id", {
      id: "decisionId",
      size: 50,
      header: () => (
        <HeaderCell className="flex items-center pl-2">
          <span className="inline-block">ID</span>
          <Tooltip placement="top" title="Decision ID">
            <div className="ml-1">
              <Icon
                color="text-gray-500"
                cursorType="pointer"
                icon={faCircleInfo}
                size="2xs"
              />
            </div>
          </Tooltip>
        </HeaderCell>
      ),
      cell: ({ cell }) => (
        <RawCell
          cell={<TruncatedIdPill id={cell.getValue()} />}
          classNameOverrides="max-w-0 pl-2"
        />
      ),
    }),
    columnHelper.accessor("start_time", {
      id: "datetime",
      size: 100,
      header: () => <HeaderCell className="w-24 pl-1">Time</HeaderCell>,
      cell: ({ cell }) => (
        <RawCell<DecisionHistoryRecordV2>
          cell={<>{formatDate(cell.getValue(), "dd MMM yy HH:mm:ss")}</>}
          classNameOverrides="max-w-40 w-32 pl-1"
        />
      ),
    }),
    columnHelper.accessor("status_code", {
      id: "status",
      size: 150,
      header: () => (
        <HeaderCell className="flex w-auto items-center pl-1">
          <span>Status</span>
          <DecisionHistoryTableFilter
            elements={statusFilterOptions}
            isFiltering={Boolean(statusCodeFilter)}
            placement="bottom start"
            selected={statusCodeFilter}
            onResetRequest={() => setStatusCodeFilter(undefined)}
            onSelect={(value: string) =>
              setStatusCodeFilter(value as DecisionStatusCode)
            }
          />
        </HeaderCell>
      ),
      cell: ({ cell }) => {
        const isCancelling =
          cancellingDecisionIds?.includes(cell.row.original.id) ?? false;
        const message = (
          <>
            <span className="mr-auto justify-start">
              <SharedDecisionHistoryStatus
                isCancelling={isCancelling}
                statusCode={cell.getValue()}
              />
            </span>
          </>
        );

        return (
          <ResultCell<DecisionHistoryRecordV2>
            cell={message}
            className="max-w-[unset] overflow-auto pl-1"
            objectDetailPosition="topRight"
          />
        );
      },
    }),
    columnHelper.accessor(
      (row) => getSourceDetails(row, parentFlows, parentVersions, jobs),
      {
        id: "source",
        size: 100,
        header: () => <HeaderCell className="w-24 pl-1">Source</HeaderCell>,
        cell: ({ cell }) => {
          const sourceDetails = cell.getValue();
          const content = (
            <SourcePill
              decisionEnv={env}
              orgId={orgId}
              sourceDetails={sourceDetails}
              wsId={wsId}
            />
          );
          return (
            <RawCell<DecisionHistoryRecordV2>
              cell={content}
              classNameOverrides="max-w-0 w-32 pl-1"
            />
          );
        },
      },
    ),
    columnHelper.accessor(
      (row) =>
        parentFlows.find((parent) => parent.id === row.parent_flow_id)?.name,
      {
        id: CONTROL_COLUMN_ID,
        size: 50,
        header: () => <></>,
        cell: ({ cell }) => {
          const content = (
            <div className="flex items-center">{getRowControls(cell)}</div>
          );
          return (
            <RawCell<DecisionHistoryRecordV2>
              cell={content}
              classNameOverrides="cell-container px-1.5"
            />
          );
        },
      },
    ),
  ] as const as ColumnDef<DecisionHistoryRecordV2<GenericObject>>[];
};
